import React, { useCallback, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FaArrowDownLong, FaArrowUpLong } from "react-icons/fa6";
import "./DataTable.scss";

const alignToClass = (align) => {
	if (align === "center") return "text-center";
	if (align === "right") return "text-end";
	return "";
};

const defaultCell = (row, columnKey) => {
	const v = row[columnKey];
	if (v == null || v === "") return null;
	return String(v);
};

function compareSortValues(a, b) {
	if (a == null && b == null) return 0;
	if (a == null) return 1;
	if (b == null) return -1;
	if (typeof a === "number" && typeof b === "number") return a - b;
	if (typeof a === "number") return -1;
	if (typeof b === "number") return 1;
	return String(a).localeCompare(String(b), "es", {
		numeric: true,
		sensitivity: "base",
	});
}

/**
 * Tabla genérica con cabecera sticky y soporte opcional de infinite scroll (mismo patrón que fichajes/supervisor).
 *
 * @param {Object} props
 * @param {{ key: string, header: React.ReactNode, render?: (row: any, rowIndex: number) => React.ReactNode, className?: string, headerClassName?: string, cellClassName?: string|((row: any, rowIndex: number) => string|undefined), cellStyle?: React.CSSProperties|((row: any, rowIndex: number) => React.CSSProperties|undefined), align?: 'left'|'center'|'right', headerTitle?: string, sortable?: boolean, sortAccessor?: (row: any) => unknown }[]} props.columns — `sortable: false` oculta flechas y desactiva el clic. Valor por defecto para ordenar: `row[col.key]`; opcional `sortAccessor`.
 * @param {any[]} props.rows
 * @param {(row: any, rowIndex: number) => string|number} [props.rowKey]
 * @param {{ enabled?: boolean, header?: React.ReactNode, render: (row: any, rowIndex: number) => React.ReactNode, className?: string, headerClassName?: string }|null} [props.actionsColumn]
 * @param {{ dataLength: number, hasMore: boolean, next: () => void, loader?: React.ReactNode, endMessage?: React.ReactNode, scrollThreshold?: number, scrollableTarget?: string }|null} [props.infiniteScroll]
 * @param {string} [props.scrollContainerId]
 * @param {string} [props.scrollContainerClassName]
 * @param {React.CSSProperties} [props.scrollContainerStyle]
 * @param {React.ReactNode} [props.emptyMessage]
 * @param {string} [props.className] — wrapper cuando no hay infinite scroll
 * @param {string} [props.tableClassName]
 * @param {React.CSSProperties} [props.tableStyle]
 * @param {(row: any, rowIndex: number) => string|undefined} [props.rowClassName]
 * @param {boolean} [props.showHeaderSortAffix=true] — Iconos de orden en cabeceras. `sortable: false` los oculta.
 * @param {{ key: string, direction: 'asc'|'desc' }|null} [props.sortState] — Orden controlado desde el padre (p. ej. API). Si se omite, la tabla ordena en cliente las filas recibidas.
 * @param {(next: { key: string, direction: 'asc'|'desc' }) => void} [props.onSortChange] — Se llama al activar orden en cabecera. Con `sortState` definido el padre debe actualizar estado y filas; si no, solo se usa para escuchar.
 * @param {(row: any, rowIndex: number) => React.ReactElement|null|undefined} [props.renderCustomRow] — Si devuelve un <tr>, sustituye la fila estándar (p. ej. colspan / filas de descanso). Si devuelve null/undefined, se usa el render por columnas.
 */
const DataTable = ({
	columns,
	rows,
	rowKey = (row, idx) => row?.id ?? idx,
	actionsColumn = null,
	infiniteScroll = null,
	scrollContainerId = "plain-data-table-scroll-target",
	scrollContainerClassName = "",
	scrollContainerStyle,
	emptyMessage = "Sin resultados.",
	className = "",
	tableClassName = "",
	tableStyle,
	rowClassName,
	showHeaderSortAffix = true,
	sortState: sortStateProp,
	onSortChange,
	renderCustomRow,
}) => {
	const [internalSort, setInternalSort] = useState(null);
	const isControlled = sortStateProp !== undefined;
	const effectiveSort = isControlled ? sortStateProp : internalSort;

	const showActions =
		actionsColumn &&
		actionsColumn.enabled !== false &&
		typeof actionsColumn.render === "function";

	const columnShowsSortAffix = (col) =>
		showHeaderSortAffix !== false && col.sortable !== false;

	const displayedRows = useMemo(() => {
		if (!rows) {
			return [];
		}
		if (isControlled) {
			return rows;
		}
		if (!internalSort) {
			return rows;
		}
		const col = columns.find((c) => c.key === internalSort.key);
		if (!col || col.sortable === false) {
			return rows;
		}
		const getVal = col.sortAccessor ?? ((r) => r[col.key]);
		const mul = internalSort.direction === "asc" ? 1 : -1;
		return [...rows].sort((r1, r2) => {
			return mul * compareSortValues(getVal(r1), getVal(r2));
		});
	}, [rows, columns, internalSort, isControlled]);

	const handleHeaderSort = useCallback(
		(col) => {
			if (!columnShowsSortAffix(col)) {
				return;
			}
			const key = col.key;
			let direction = "asc";
			if (effectiveSort && effectiveSort.key === key) {
				direction = effectiveSort.direction === "asc" ? "desc" : "asc";
			}
			const next = { key, direction };
			if (onSortChange) {
				onSortChange(next);
			}
			if (!isControlled) {
				setInternalSort(next);
			}
		},
		[
			columnShowsSortAffix,
			effectiveSort,
			isControlled,
			onSortChange,
		],
	);

	const renderSortAffix = (col) => {
		if (!columnShowsSortAffix(col)) {
			return null;
		}
		const dir =
			effectiveSort && effectiveSort.key === col.key
				? effectiveSort.direction
				: null;
		if (dir === "asc") {
			return (
				<span className="plain-data-table__sort" aria-hidden>
					<FaArrowUpLong className="plain-data-table__sort-icon plain-data-table__sort-icon--active" />
				</span>
			);
		}
		if (dir === "desc") {
			return (
				<span className="plain-data-table__sort" aria-hidden>
					<FaArrowDownLong className="plain-data-table__sort-icon plain-data-table__sort-icon--active" />
				</span>
			);
		}
		return (
			<span className="plain-data-table__sort" aria-hidden>
				<FaArrowDownLong className="plain-data-table__sort-icon" />
				<FaArrowUpLong className="plain-data-table__sort-icon" />
			</span>
		);
	};

	const table = (
		<table
			className={`table plain-data-table ${tableClassName}`.trim()}
			style={tableStyle}
		>
			<thead>
				<tr>
					{columns.map((col) => {
						const showSortUi = columnShowsSortAffix(col);
						const sortClickable = showSortUi;
						const ariaSort =
							effectiveSort && effectiveSort.key === col.key
								? effectiveSort.direction === "asc"
									? "ascending"
									: "descending"
								: "none";
						return (
							<th
								key={col.key}
								title={col.headerTitle}
								scope="col"
								aria-sort={showSortUi ? ariaSort : undefined}
								className={
									[
										col.headerClassName,
										alignToClass(col.align),
										showSortUi ? "plain-data-table__th--sort" : "",
										sortClickable ? "plain-data-table__th--sortable" : "",
									]
										.filter(Boolean)
										.join(" ") || undefined
								}
								onClick={
									sortClickable
										? () => handleHeaderSort(col)
										: undefined
								}
								onKeyDown={
									sortClickable
										? (e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleHeaderSort(col);
												}
											}
										: undefined
								}
								role={sortClickable ? "columnheader" : undefined}
								tabIndex={sortClickable ? 0 : undefined}
							>
								<span className="plain-data-table__th-inner">
									<span className="plain-data-table__th-label">{col.header}</span>
									{renderSortAffix(col)}
								</span>
							</th>
						);
					})}
					{showActions && (
						<th
							className={
								[
									"plain-data-table__actions-header",
									actionsColumn.headerClassName,
								]
									.filter(Boolean)
									.join(" ") || undefined
							}
						>
							{actionsColumn.header ?? ""}
						</th>
					)}
				</tr>
			</thead>
			<tbody>
				{displayedRows.length === 0 ? (
					<tr>
						<td
							colSpan={columns.length + (showActions ? 1 : 0)}
							className="plain-data-table__empty"
						>
							{emptyMessage}
						</td>
					</tr>
				) : (
					displayedRows.map((row, rowIndex) => {
						if (typeof renderCustomRow === "function") {
							const custom = renderCustomRow(row, rowIndex);
							if (custom != null) {
								return React.cloneElement(custom, {
									key: rowKey(row, rowIndex),
								});
							}
						}
						return (
							<tr
								key={rowKey(row, rowIndex)}
								className={
									rowClassName ? rowClassName(row, rowIndex) : undefined
								}
							>
								{columns.map((col) => {
									const dynamicCellClass =
										typeof col.cellClassName === "function"
											? col.cellClassName(row, rowIndex)
											: col.cellClassName;
									const dynamicCellStyle =
										typeof col.cellStyle === "function"
											? col.cellStyle(row, rowIndex)
											: col.cellStyle;
									const cellClass =
										[
											dynamicCellClass,
											col.className,
											alignToClass(col.align),
										]
											.filter(Boolean)
											.join(" ") || undefined;
									return (
										<td
											key={col.key}
											className={cellClass}
											style={dynamicCellStyle || undefined}
										>
											{col.render
												? col.render(row, rowIndex)
												: defaultCell(row, col.key)}
										</td>
									);
								})}
								{showActions && (
									<td
										className={
											[
												"plain-data-table__actions-cell",
												actionsColumn.className,
											]
												.filter(Boolean)
												.join(" ") || undefined
										}
									>
										{actionsColumn.render(row, rowIndex)}
									</td>
								)}
							</tr>
						);
					})
				)}
			</tbody>
		</table>
	);

	if (infiniteScroll) {
		const {
			dataLength,
			hasMore,
			next,
			loader,
			endMessage,
			scrollThreshold = 0.9,
			scrollableTarget = scrollContainerId,
		} = infiniteScroll;

		return (
			<div
				id={scrollContainerId}
				className={`plain-data-table-scroll ${scrollContainerClassName} ${className}`.trim()}
				style={scrollContainerStyle}
			>
				<InfiniteScroll
					dataLength={dataLength}
					next={next}
					hasMore={hasMore}
					loader={loader}
					endMessage={endMessage}
					scrollThreshold={scrollThreshold}
					scrollableTarget={scrollableTarget}
				>
					{table}
				</InfiniteScroll>
			</div>
		);
	}

	return (
		<div className={`plain-data-table-outer ${className}`.trim()}>{table}</div>
	);
};

export default DataTable;
